import { CarrierBillingAccountType } from "@redotech/cockroachdb-codegen/congodb/carrier-billing-account-type";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";

import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import {
  RedoModalFooter,
  RedoModalHeader,
} from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { memo } from "react";
import {
  billingAccountDefault,
  BillingAccountInput,
  CreateBillingAccountForm,
} from "./billing-account-form";
import { AddBillingAccountData } from "./billing-accounts";

interface AddBillingAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddAccount: (
    account: AddBillingAccountData,
    options?: { hidden?: boolean },
  ) => Promise<{ success: boolean }>;
  billingType: CarrierBillingAccountType;
  showUseOnceOption?: boolean;
}

const COPY: Record<
  CarrierBillingAccountType,
  { title: string; subtitle: string }
> = {
  [CarrierBillingAccountType.ThirdParty]: {
    title: "Add third party billing account",
    subtitle:
      "Add third-party accounts to bill for specific shipments. Please ensure the account information is correct or you will not be able to buy labels.",
  },
  [CarrierBillingAccountType.Receiver]: {
    title: "Add receiver billing account",
    subtitle:
      "Bill the recipient's own carrier account for specific shipments. Please ensure the account information is correct or you will not be able to buy labels.",
  },
};

export const AddBillingAccountModal = memo(function AddBillingAccountModal({
  isOpen,
  onClose,
  onAddAccount,
  billingType,
  showUseOnceOption,
}: AddBillingAccountModalProps) {
  const form = useInput(BillingAccountInput, billingAccountDefault());

  const getAccountData = (): AddBillingAccountData => ({
    accountName: form.inputs.accountName.value,
    carrier: form.inputs.carrier.value,
    accountNumber: form.inputs.accountNumber.value,
    country: form.inputs.country.value,
    postalCode: form.inputs.postalCode.value,
    billingType,
  });

  const handleSubmit = useHandler(async () => {
    const result = await onAddAccount(getAccountData());
    if (result.success) {
      form.setValue(billingAccountDefault());
      onClose();
    }
  });

  const handleUseOnce = useHandler(async () => {
    const result = await onAddAccount(getAccountData(), { hidden: true });
    if (result.success) {
      form.setValue(billingAccountDefault());
      onClose();
    }
  });

  const canSubmit = form.allErrors.length === 0;
  const copy = COPY[billingType];

  return (
    <RedoBaseModal isOpen={isOpen} onModalCloseRequested={onClose}>
      <RedoModalHeader
        cancelClicked={onClose}
        subtitle={copy.subtitle}
        title={copy.title}
      />

      <Flex dir="column" gap="xl" p="xl">
        <CreateBillingAccountForm billingType={billingType} form={form} />
      </Flex>

      {showUseOnceOption ? (
        <RedoModalFooter
          primaryButton={{
            disabled: !canSubmit,
            onClick: handleSubmit,
            text: "Save & use",
            size: "sm",
          }}
          secondaryButton={{
            disabled: !canSubmit,
            onClick: handleUseOnce,
            text: "Use once",
            size: "sm",
          }}
        />
      ) : (
        <RedoModalFooter
          primaryButton={{
            disabled: !canSubmit,
            onClick: handleSubmit,
            text: "Submit",
          }}
          secondaryButton={{ onClick: onClose, text: "Cancel" }}
        />
      )}
    </RedoBaseModal>
  );
});
